import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import { useLeoChatStore, type TLeoChatMessage } from "@/zapi-frontend/stores/leoChatStore";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import {
  startLeoChatTask,
  cancelLeoChatTask,
  getActiveLeoChatTask,
  getLeoChatHistory,
  getLeoChatTaskStatus,
  clearLeoChatHistory,
  uploadLeoImage
} from "../api/leoChat";
import { followedTask, historyMessages } from "../lib/leoHistory";
import type { TStagedLeoImage } from "./useLeoImages";

const EMPTY_MESSAGES: TLeoChatMessage[] = [];

// "This chapter" in a message means the one on screen, so a turn sent from a chapter says which.
const openChapterSlug = (): string | undefined => {
  const { viewMode, activeChapter } = useChapterStore.getState();
  return viewMode === "chapter" ? activeChapter?.projectTemplateChapter.slug || undefined : undefined;
};

const extractErrorMessage = (error: unknown): string | null => {
  if (isAxiosError(error)) {
    const message = (error.response?.data as { message?: unknown } | undefined)?.message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return null;
};

// The running turn itself is followed by useLeoTaskPolling on the project page; the panel sends,
// stops and clears, and reads the turn's state from the store.
// From the presentation outline, "this slide" is the one selected there instead.
export function useLeoChat(
  projectId: string,
  { canEdit: editingAvailable = true, slideIndex }: { canEdit?: boolean; slideIndex?: number } = {}
) {
  const { t } = useTranslation("projectHome");
  const messages = useLeoChatStore((s) => s.historyByProject[projectId] ?? EMPTY_MESSAGES);
  const activeTask = useLeoChatStore((s) => s.activeTaskByProject[projectId] ?? null);
  const addMessage = useLeoChatStore((s) => s.addMessage);
  const setActiveTask = useLeoChatStore((s) => s.setActiveTask);
  const clearHistory = useLeoChatStore((s) => s.clearHistory);
  const setHistory = useLeoChatStore((s) => s.setHistory);

  const { role, accessMode } = useMeStore();
  // Leo decides per message whether to answer or edit; this only says whether editing is on the
  // table — the page has chapters to edit and the user is allowed to change them.
  const canEdit = editingAvailable && hasEditAccess(role, accessMode);

  const isStarting = useLeoChatStore((s) => !!s.startingByProject[projectId]);
  const setStarting = useLeoChatStore((s) => s.setStarting);
  const status = activeTask?.liveStatus ?? null;

  const activeTaskId = activeTask?.id ?? null;

  useEffect(() => {
    if (!projectId) return;
    if (useLeoChatStore.getState().historyByProject[projectId]?.length) return;
    let cancelled = false;
    getLeoChatHistory(projectId)
      .then((items) => {
        if (cancelled) return;
        if (useLeoChatStore.getState().historyByProject[projectId]?.length) return;
        setHistory(projectId, historyMessages(items));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [projectId, setHistory]);

  // Resolves to the message when it was not sent because a turn is still running, so the composer
  // can hand it back to the reader.
  const send = useCallback(
    async (rawMessage: string, images: TStagedLeoImage[] = []): Promise<string | null> => {
      const message = rawMessage.trim();
      if ((!message && images.length === 0) || isStarting || activeTaskId) return null;
      const startedAt = Date.now();
      const historyBefore = useLeoChatStore.getState().historyByProject[projectId] ?? EMPTY_MESSAGES;
      addMessage(projectId, {
        role: "user",
        content: message,
        ...(images.length > 0
          ? { images: images.map((image) => ({ name: image.file.name, url: image.previewUrl })) }
          : {})
      });
      setStarting(projectId, true);
      try {
        const uploaded = await Promise.all(images.map((image) => uploadLeoImage(projectId, image.file)));
        const taskId = await startLeoChatTask({
          projectId: Number(projectId),
          message,
          canEdit,
          launch: "cli",
          ...(slideIndex === undefined ? { chapterSlug: openChapterSlug() } : { slideIndex }),
          ...(uploaded.length > 0 ? { images: uploaded } : {})
        });
        setActiveTask(projectId, {
          id: taskId,
          canEdit,
          status: "pending",
          progressText: null,
          startedAt,
          liveStatus: null,
          editedChapters: [],
          editedHome: false
        });
        return null;
      } catch (error) {
        const running =
          isAxiosError(error) && error.response?.status === 409
            ? await getActiveLeoChatTask(projectId).catch(() => null)
            : null;
        if (running?.id) {
          // Usually the platform's follow-up turn: the server's history carries its prompt, so its
          // reply is not read as the answer to the message that was refused.
          const items = await getLeoChatHistory(projectId).catch(() => null);
          setHistory(projectId, items ? historyMessages(items) : historyBefore);
          if (!useLeoChatStore.getState().activeTaskByProject[projectId]) {
            setActiveTask(projectId, followedTask({ ...running, id: running.id }));
          }
          toast(t("leo.stillRunning"));
          return message;
        }
        addMessage(projectId, {
          role: "assistant",
          content: extractErrorMessage(error) ?? "Failed to start the chat task. Please try again.",
          isError: true
        });
      } finally {
        setStarting(projectId, false);
      }
      return null;
    },
    [projectId, canEdit, slideIndex, isStarting, activeTaskId, addMessage, setActiveTask, setHistory, setStarting, t]
  );

  const stop = useCallback(async () => {
    if (!activeTaskId) return;
    try {
      await cancelLeoChatTask(activeTaskId);
      // A turn can finish while the cancel waits on it; the poll then shows its reply and changes.
      const final = await getLeoChatTaskStatus(activeTaskId).catch(() => null);
      if (final && final.status !== "cancelled") return;
      // A concurrent poll may have already delivered the terminal message and cleared the task.
      if (useLeoChatStore.getState().activeTaskByProject[projectId]?.id !== activeTaskId) return;
      addMessage(projectId, { role: "assistant", content: "Stopped.", isError: true });
      setActiveTask(projectId, null);
    } catch (error) {
      // 409 = the task finished right before the cancel; the poll will show the real result.
      if (isAxiosError(error) && error.response?.status === 409) return;
      addMessage(projectId, {
        role: "assistant",
        content: extractErrorMessage(error) ?? "Could not stop the task.",
        isError: true
      });
    }
  }, [projectId, activeTaskId, addMessage, setActiveTask]);

  const clear = useCallback(() => {
    if (activeTaskId) return;
    clearHistory(projectId);
    clearLeoChatHistory(projectId).catch(() => undefined);
  }, [projectId, activeTaskId, clearHistory]);

  return {
    messages,
    activeTask,
    isBusy: isStarting || !!activeTaskId,
    status,
    canEdit,
    send,
    stop,
    clear
  };
}
